import { useSelector } from 'react-redux';
import { UserService } from '../core/services';
import { FORCE_US_INFLUENCER_MENU } from '../config/config';

const userService = new UserService();

const US_COUNTRY_NAMES = ['united states', 'united states of america', 'usa', 'us'];

// Cached per influencer_id so the header and mobile footer (mounted separately)
// don't both hit the influencer-details API on every page load.
const countryCache = new Map<string, Promise<string>>();

const fetchCountry = (influencer_id: string): Promise<string> => {
    if (!countryCache.has(influencer_id)) {
        countryCache.set(
            influencer_id,
            userService.getInfluencerDetails(influencer_id)
                .then((res: any) => res?.data?.rows?.[0]?.country || '')
                .catch(() => '')
        );
    }
    return countryCache.get(influencer_id)!;
};

const isUSCountryName = (country: string) => US_COUNTRY_NAMES.includes((country || '').trim().toLowerCase());

// FOREIGN (Zap-only) iff the phone prefix (dialing_code) is not +91. This mirrors
// the geo/foreign logic used across pricing, subscription and the mobile app, and
// is far more reliable than the profile `country` field (which is often stale —
// e.g. a US user whose auth_accounts.country still reads India). +91 or empty =>
// India / normal flow.
export const isForeignDialCode = (dialCode?: string | null): boolean => {
    const digits = String(dialCode ?? '').replace(/[^0-9]/g, '');
    return !!digits && digits !== '91';
};

// Plain (non-hook) check by influencer_id, used for OTHER users (switch-account /
// switch-user flows) where the target's data isn't in Redux yet. Still country-
// based since only the id is known here.
export const checkIsUSInfluencer = (influencer_id: string | number): Promise<boolean> => {
    if (FORCE_US_INFLUENCER_MENU) return Promise.resolve(true);
    return fetchCountry(String(influencer_id)).then(isUSCountryName);
};

// True when the LOGGED-IN influencer is foreign (Zap-only). Reads the reliable
// dialing_code from Redux — synchronous, no API call, no flash. (Name kept for
// back-compat; it now means "is foreign influencer", not strictly US.)
export const useIsUSInfluencer = (): boolean => {
    const dialingCode = useSelector((state: any) => state.UserReducer?.UserData?.dialing_code);
    if (FORCE_US_INFLUENCER_MENU) return true;
    return isForeignDialCode(dialingCode);
};
